import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { vmixInstancesAPI, showsAPI, vmixAPI, vmixPresetsAPI } from '../lib/api';
import { invalidatePresetScripts } from '../lib/vmixPresetScripts';
import { parsePreset, serializePreset, spliceScripting, uniqueScriptName, duplicateNames, newScriptId } from '../lib/vmixPreset';
import { parseScript, serializeScript, countFunctions, dedupeWebRequestVars, makeFunctionRow, cloneRow } from '../lib/vmixScript';
import ScriptActionsView from '../components/tools/ScriptActionsView';
import FunctionCombobox from '../components/rundown/FunctionCombobox';

// vMix Script Editor.
//
// vMix's own script editor is one script at a time, in a small modal, with no
// way to duplicate a script or change the same call across twenty of them. But
// the scripts are just XML inside the preset, so this drops the whole preset in
// the browser, lists every script, and edits them either as raw VB.NET or as a
// rundown-style action list.
//
// There are two ways in, and the difference matters for download:
//
//   Local file — the whole .vmix is in memory. Edit, download, done; nothing
//     leaves the browser.
//   From the library — only the <Scripting> block was ever stored server-side,
//     because the rest of a preset carries streaming destinations and stream
//     keys. So the editor has the scripts but NOT the other ~93% of the file,
//     and downloading asks the operator for their own copy to splice into.
//     `source_hash` is how we can tell them it is the wrong file.
//
// Either way only the <Scripting> block is ever rewritten — see vmixPreset.js.

/** sha256 of the text, hex — matches how the server hashes an uploaded preset. */
async function sha256Hex(text) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, '0')).join('');
}

function saveFile(text, name) {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/xml' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name.replace(/\.(vmix|txt|xml)$/i, '') + '.vmix';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function VmixScriptEditorPage() {
  const [preset, setPreset] = useState(null);
  const [fileName, setFileName] = useState('');
  const [scripts, setScripts] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [checked, setChecked] = useState(() => new Set());   // scripts ticked for bulk ops
  const [search, setSearch] = useState('');
  const [view, setView] = useState('actions');               // 'actions' | 'raw'

  const [rows, setRows] = useState([]);
  const [rowsFor, setRowsFor] = useState(null);              // which script `rows` was parsed from
  const [checkedRows, setCheckedRows] = useState(() => new Set());

  const [instances, setInstances] = useState([]);
  const [findOpen, setFindOpen] = useState(false);
  const [runOpen, setRunOpen] = useState(false);

  // The library record this preset came from (or was saved to), and whether we
  // still hold the whole .vmix. Loading from the library gives us the scripts
  // without the file around them, which is what download has to cope with.
  const [library, setLibrary] = useState(null);
  const [hasFullFile, setHasFullFile] = useState(false);
  const [saving, setSaving] = useState('');            // '' | 'saving' | 'saved'
  const [spliceOpen, setSpliceOpen] = useState(false);

  const rawRef = useRef(null);

  useEffect(() => {
    vmixInstancesAPI.list().then(r => setInstances(r.data || [])).catch(() => {});
  }, []);

  // Leaving with unsaved edits loses them — there is no server-side copy.
  useEffect(() => {
    if (!dirty) return;
    const warn = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const selected = scripts.find(s => s.id === selectedId) || null;
  const dupes = useMemo(() => duplicateNames(scripts), [scripts]);

  // Re-parse into rows when the actions view needs a script it hasn't got.
  useEffect(() => {
    if (view !== 'actions' || !selected) return;
    if (rowsFor === selected.id) return;
    setRows(parseScript(selected.code));
    setRowsFor(selected.id);
    setCheckedRows(new Set());
  }, [view, selected, rowsFor]);

  function adopt(parsed, name, { fromLibrary = null, full }) {
    setPreset(parsed);
    setScripts(parsed.scripts);
    setFileName(name);
    setLibrary(fromLibrary);
    setHasFullFile(full);
    setSelectedId(parsed.scripts[0]?.id ?? null);
    setRowsFor(null);
    setChecked(new Set());
    setDirty(false);
    setError('');
  }

  function loadFile(file) {
    if (dirty && !window.confirm('Discard unsaved changes to the current preset?')) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        adopt(parsePreset(String(reader.result)), file.name, { full: true });
      } catch (err) {
        setError(err.message);
      }
    };
    reader.onerror = () => setError('Could not read that file.');
    reader.readAsText(file);
  }

  async function loadFromLibrary(entry) {
    if (dirty && !window.confirm('Discard unsaved changes to the current preset?')) return;
    try {
      const { data } = await vmixPresetsAPI.get(entry.id, { includeScripting: true });
      // The stored slice parses exactly like a whole file — there is simply
      // nothing around the <Scripting> block. serializePreset then emits that
      // block on its own, which is what spliceScripting wants.
      adopt(parsePreset(data.scripting), data.file_name, { fromLibrary: data, full: false });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not open that preset.');
    }
  }

  async function saveToLibrary() {
    setSaving('saving');
    setError('');
    try {
      const payload = scripts.map(s => ({ name: s.name, code: s.code }));
      if (library) {
        await vmixPresetsAPI.saveScripts(library.id, payload);
      } else {
        // Not in the library yet, and we still hold the whole file — upload it
        // and let the server keep only the scripting block.
        const text = serializePreset(preset, scripts);
        const file = new File([text], fileName || 'preset.vmix', { type: 'application/xml' });
        const { data } = await vmixPresetsAPI.upload(file, fileName.replace(/\.(vmix|txt|xml)$/i, ''));
        setLibrary(data);
      }
      // The cue editor's ScriptStart dropdown reads these names from a cached
      // library, so a rename here has to reach it without a page reload.
      invalidatePresetScripts();
      setDirty(false);
      setSaving('saved');
      setTimeout(() => setSaving(s => (s === 'saved' ? '' : s)), 2000);
    } catch (err) {
      setSaving('');
      setError(err.response?.data?.error || err.message || 'Could not save to the library.');
    }
  }

  const patchScript = useCallback((id, patch) => {
    setScripts(prev => prev.map(s => (s.id === id ? { ...s, ...patch } : s)));
    setDirty(true);
  }, []);

  // Rows are the working copy while the actions view is open; every change is
  // serialised straight back to the script's code so the raw view and the
  // download never see a stale version.
  //
  // This is also the one choke point for row edits, which is why the dedupe
  // lives here: retargeting a call to another vMix turns it into a WebRequest
  // block, and two blocks declaring `client1` stop the script compiling at all.
  function updateRows(next) {
    const safe = dedupeWebRequestVars(next);
    setRows(safe);
    if (selected) patchScript(selected.id, { code: serializeScript(safe) });
  }

  function updateCode(code) {
    if (!selected) return;
    patchScript(selected.id, { code });
    setRowsFor(null);        // raw text changed — actions must re-parse
  }

  function selectScript(id) {
    setSelectedId(id);
    setRowsFor(null);
  }

  // ------------------------------------------------------------ script ops --

  function addScript() {
    const s = { id: newScriptId(), name: uniqueScriptName('New Script', scripts), code: '' };
    setScripts([...scripts, s]);
    setSelectedId(s.id);
    setRowsFor(null);
    setDirty(true);
  }

  function duplicateScripts(ids) {
    let next = [...scripts];
    let lastNewId = null;
    for (const id of ids) {
      const idx = next.findIndex(s => s.id === id);
      if (idx === -1) continue;
      const src = next[idx];
      const copy = { id: newScriptId(), name: uniqueScriptName(src.name, next), code: src.code };
      lastNewId = copy.id;
      next.splice(idx + 1, 0, copy);
    }
    setScripts(next);
    if (lastNewId) { setSelectedId(lastNewId); setRowsFor(null); }
    setChecked(new Set());
    setDirty(true);
  }

  function deleteScripts(ids) {
    const set = new Set(ids);
    const names = scripts.filter(s => set.has(s.id)).map(s => s.name);
    if (!window.confirm(`Delete ${names.length === 1 ? `"${names[0]}"` : `${names.length} scripts`}?`)) return;
    const next = scripts.filter(s => !set.has(s.id));
    setScripts(next);
    if (set.has(selectedId)) { setSelectedId(next[0]?.id ?? null); setRowsFor(null); }
    setChecked(new Set());
    setDirty(true);
  }

  // ---------------------------------------------------------------- insert --

  // Drop a freshly built function in at the cursor (raw view) or after the last
  // ticked row (actions view).
  function insertFunction({ fn, input, value, target }) {
    if (!selected) return;

    if (view === 'actions') {
      const row = makeFunctionRow({ fn, input, value, target });
      const lastChecked = rows.reduce((acc, r, i) => (checkedRows.has(r.id) ? i : acc), -1);
      const at = lastChecked === -1 ? rows.length : lastChecked + 1;
      const next = [...rows];
      next.splice(at, 0, row);
      updateRows(next);
      return;
    }

    const snippet = serializeScript([makeFunctionRow({ fn, input, value, target })]);
    const ta = rawRef.current;
    const code = selected.code;
    // With no textarea focus, append; otherwise splice in at the caret on its
    // own line so we never land mid-statement.
    const pos = ta ? ta.selectionStart : code.length;
    const before = code.slice(0, pos);
    const after = code.slice(pos);
    const lead = before === '' || before.endsWith('\n') ? '' : '\n';
    const trail = after === '' || after.startsWith('\n') ? '' : '\n';
    const next = `${before}${lead}${snippet}${trail}${after}`;
    updateCode(next);
    requestAnimationFrame(() => {
      if (!ta) return;
      const caret = (before + lead + snippet).length;
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  }

  // -------------------------------------------------------------- download --

  function download() {
    if (!hasFullFile) {
      // Only the scripting block is in memory. The operator's own .vmix has to
      // supply every other byte — which is the whole point of never storing it.
      setSpliceOpen(true);
      return;
    }
    saveFile(serializePreset(preset, scripts), fileName);
    setDirty(false);
  }

  // Splice the edited scripts into the operator's copy of the preset.
  async function downloadInto(file) {
    try {
      const text = await file.text();
      if (library?.source_hash) {
        const digest = await sha256Hex(text);
        if (digest !== library.source_hash) {
          const ok = window.confirm(
            `"${file.name}" is not the file these scripts were taken from.\n\n` +
              `Its inputs, outputs and streaming settings will be kept and only the scripts ` +
              `replaced — but if the two presets have different inputs, the scripts may now ` +
              `point at the wrong ones.\n\nContinue anyway?`
          );
          if (!ok) return;
        }
      }
      // `preset` here is the stored block, so this serialises the block alone.
      saveFile(spliceScripting(text, serializePreset(preset, scripts)), file.name);
      setSpliceOpen(false);
      setDirty(false);
      setError('');
    } catch (err) {
      setError(err.message || 'Could not read that file.');
    }
  }

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scripts;
    return scripts.filter(s =>
      s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q));
  }, [scripts, search]);

  if (!preset) {
    return <DropZone onFile={loadFile} onOpen={loadFromLibrary} error={error} />;
  }

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 3rem)' }}>
      <Header
        fileName={fileName}
        scriptCount={scripts.length}
        dirty={dirty}
        library={library}
        hasFullFile={hasFullFile}
        saving={saving}
        onSave={saveToLibrary}
        onReplace={loadFile}
        onDownload={download}
        onFind={() => setFindOpen(true)}
        onRun={() => setRunOpen(true)}
        canRun={!!selected}
      />

      {error && (
        <div className="mx-4 mt-3 px-3 py-2 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        <ScriptList
          scripts={visible}
          total={scripts.length}
          selectedId={selectedId}
          checked={checked}
          setChecked={setChecked}
          dupes={dupes}
          search={search}
          setSearch={setSearch}
          onSelect={selectScript}
          onAdd={addScript}
          onDuplicate={duplicateScripts}
          onDelete={deleteScripts}
        />

        <div className="flex-1 flex flex-col min-w-0">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-gray-600 text-sm">
              {scripts.length === 0 ? 'This preset has no scripts yet.' : 'Select a script.'}
            </div>
          ) : (
            <>
              <ScriptToolbar
                script={selected}
                duplicateName={dupes.has(selected.name)}
                view={view}
                setView={setView}
                onRename={name => patchScript(selected.id, { name })}
                rowCount={view === 'actions' ? rows.length : selected.code.split('\n').length}
                functionCount={view === 'actions' ? countFunctions(rows) : null}
              />

              <BuilderBar instances={instances} onInsert={insertFunction} view={view} />

              {checkedRows.size > 0 && view === 'actions' && (
                <BulkRowBar
                  count={checkedRows.size}
                  instances={instances}
                  rows={rows}
                  checkedRows={checkedRows}
                  setCheckedRows={setCheckedRows}
                  updateRows={updateRows}
                />
              )}

              <div className="flex-1 overflow-y-auto">
                {view === 'raw' ? (
                  <textarea
                    ref={rawRef}
                    value={selected.code}
                    onChange={e => updateCode(e.target.value)}
                    spellCheck={false}
                    placeholder="' vMix VB.NET script"
                    className="w-full h-full min-h-[400px] bg-app-canvas text-app-fg font-mono text-xs leading-5 p-4 resize-none focus:outline-none"
                  />
                ) : (
                  <ScriptActionsView
                    rows={rows}
                    setRows={updateRows}
                    instances={instances}
                    selected={checkedRows}
                    setSelected={setCheckedRows}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {findOpen && (
        <FindReplaceModal
          scripts={scripts}
          checked={checked}
          selectedId={selectedId}
          onClose={() => setFindOpen(false)}
          onApply={next => {
            setScripts(next);
            setRowsFor(null);
            setDirty(true);
            setFindOpen(false);
          }}
        />
      )}

      {runOpen && selected && (
        <RunModal script={selected} instances={instances} onClose={() => setRunOpen(false)} />
      )}

      {spliceOpen && (
        <SpliceModal
          library={library}
          onClose={() => setSpliceOpen(false)}
          onFile={downloadInto}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ chrome --

function DropZone({ onFile, onOpen, error }) {
  const [over, setOver] = useState(false);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  const refresh = useCallback(() => {
    vmixPresetsAPI.list()
      .then(r => setLibrary(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function remove(entry) {
    if (!window.confirm(`Remove "${entry.name}" from the library?\n\nYour own .vmix file is untouched — only the stored scripts are deleted.`)) return;
    try {
      await vmixPresetsAPI.remove(entry.id);
      refresh();
    } catch {
      /* the list simply stays as it was */
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <h1 className="text-xl font-bold text-white">vMix Script Editor</h1>
      <p className="text-gray-500 text-sm mt-0.5 mb-6">
        Edit every script in a vMix preset at once — duplicate them, retarget them, and build
        functions without memorising the syntax.
      </p>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">
          {error}
        </div>
      )}

      <div
        onDragOver={e => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={e => {
          e.preventDefault();
          setOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) onFile(file);
        }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg px-8 py-16 text-center cursor-pointer transition-colors ${
          over ? 'border-blue-500 bg-blue-950/20' : 'border-app-border hover:border-app-border-strong'
        }`}
      >
        <svg className="w-10 h-10 mx-auto text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M7 16a4 4 0 01-.88-7.9A5 5 0 1115.9 6H16a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <div className="text-app-fg text-sm font-medium">Drop a vMix preset here</div>
        <div className="text-gray-600 text-xs mt-1">or click to choose a .vmix file</div>
        <input
          ref={inputRef}
          type="file"
          accept=".vmix,.xml,.txt"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
        />
      </div>

      <p className="text-gray-600 text-xs mt-4 leading-relaxed">
        The preset is read in your browser. When you download it back, only the scripting section is
        rewritten — every input, mixer and output setting comes back untouched.
      </p>

      <div className="mt-10">
        <div className="flex items-baseline gap-2 mb-1">
          <h2 className="text-sm font-medium text-white">Library</h2>
          <span className="text-[11px] text-gray-600">
            {loading ? 'loading…' : `${library.length} preset${library.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <p className="text-gray-600 text-xs mb-3 leading-relaxed">
          Saving a preset here stores <strong className="text-gray-500">only its scripts</strong>, so they
          can be bulk-edited from anywhere — including by Claude. Inputs, outputs and streaming
          settings, which is where your stream keys live, are discarded on upload and never stored.
        </p>

        {!loading && library.length === 0 ? (
          <div className="border border-app-border rounded px-4 py-6 text-center text-xs text-gray-600">
            Nothing saved yet. Open a preset and choose <span className="text-gray-500">Save to library</span>.
          </div>
        ) : (
          <div className="border border-app-border rounded divide-y divide-app-border">
            {library.map(p => (
              <div key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-app-row-hover group">
                <button onClick={() => onOpen(p)} className="min-w-0 flex-1 text-left">
                  <div className="text-xs text-gray-300 truncate">{p.name}</div>
                  <div className="text-[10px] text-gray-600 truncate">
                    {p.script_count} script{p.script_count === 1 ? '' : 's'} · {p.file_name} ·
                    updated {new Date(p.updated_at).toLocaleDateString()}
                  </div>
                </button>
                <button
                  onClick={() => onOpen(p)}
                  className="text-[11px] text-gray-400 hover:text-app-fg flex-shrink-0"
                >
                  Open
                </button>
                <button
                  onClick={() => remove(p)}
                  className="text-[11px] text-gray-600 hover:text-red-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Header({
  fileName, scriptCount, dirty, library, hasFullFile, saving,
  onSave, onReplace, onDownload, onFind, onRun, canRun,
}) {
  const inputRef = useRef(null);
  const btn = 'px-3 py-1.5 text-xs rounded border border-app-border text-gray-400 hover:text-app-fg hover:border-app-border-strong transition-colors';

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-app-line-hard bg-app-chrome flex-shrink-0">
      <span className="text-sm font-medium text-white truncate max-w-xs" title={fileName}>{fileName}</span>
      <span className="text-xs text-gray-600">{scriptCount} script{scriptCount === 1 ? '' : 's'}</span>
      {dirty && <span className="text-xs text-amber-500">● unsaved</span>}
      {library && (
        <span
          className="text-[11px] text-gray-600"
          title={hasFullFile
            ? 'Saved in the library. The whole file is still open here, so Download works straight away.'
            : 'Opened from the library, so only the scripts are loaded. Download will ask for your own .vmix to splice them into.'}
        >
          in library{!hasFullFile && ' · scripts only'}
        </span>
      )}

      <div className="ml-auto flex items-center gap-2">
        <button className={btn} onClick={onFind}>Find &amp; Replace</button>
        <button className={btn} onClick={onRun} disabled={!canRun}>Test run…</button>
        <button className={btn} onClick={() => inputRef.current?.click()}>Open another…</button>
        <button className={btn} onClick={onSave} disabled={saving === 'saving'}>
          {saving === 'saving' ? 'Saving…' : saving === 'saved' ? 'Saved ✓' : library ? 'Save to library' : 'Save to library…'}
        </button>
        <button
          onClick={onDownload}
          title={hasFullFile ? undefined : 'Only the scripts are loaded — you will be asked for your own .vmix to splice them into.'}
          className="px-3 py-1.5 text-xs rounded bg-blue-600 hover:bg-blue-500 text-white transition-colors"
        >
          {hasFullFile ? 'Download .vmix' : 'Download .vmix…'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".vmix,.xml,.txt"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) onReplace(f); e.target.value = ''; }}
        />
      </div>
    </div>
  );
}

function ScriptList({
  scripts, total, selectedId, checked, setChecked, dupes,
  search, setSearch, onSelect, onAdd, onDuplicate, onDelete,
}) {
  const allChecked = scripts.length > 0 && scripts.every(s => checked.has(s.id));

  function toggle(id) {
    const next = new Set(checked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setChecked(next);
  }

  return (
    <div className="w-72 flex-shrink-0 border-r border-app-line-hard flex flex-col bg-app-chrome-sunk">
      <div className="p-2 border-b border-app-border flex-shrink-0">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search names and code…"
          className="w-full bg-app-surface border border-app-border rounded px-2 py-1 text-xs text-app-fg focus:outline-none focus:border-blue-500"
        />
        {search && (
          <div className="text-[10px] text-gray-600 mt-1">{scripts.length} of {total} match</div>
        )}
      </div>

      <div className="flex items-center gap-2 px-2 py-1.5 border-b border-app-border flex-shrink-0">
        <input
          type="checkbox"
          checked={allChecked}
          onChange={() => setChecked(allChecked ? new Set() : new Set(scripts.map(s => s.id)))}
          title="Select all shown"
          className="accent-blue-600"
        />
        {checked.size > 0 ? (
          <>
            <span className="text-[11px] text-gray-400">{checked.size} selected</span>
            <button
              onClick={() => onDuplicate([...checked])}
              className="ml-auto text-[11px] text-gray-400 hover:text-app-fg"
            >
              Duplicate
            </button>
            <button
              onClick={() => onDelete([...checked])}
              className="text-[11px] text-gray-400 hover:text-red-400"
            >
              Delete
            </button>
          </>
        ) : (
          <button onClick={onAdd} className="ml-auto text-[11px] text-gray-400 hover:text-app-fg">
            + New script
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {scripts.map(s => (
          <div
            key={s.id}
            onClick={() => onSelect(s.id)}
            className={`group flex items-center gap-2 px-2 py-1.5 cursor-pointer border-l-2 ${
              s.id === selectedId
                ? 'bg-app-row-hover border-blue-500'
                : 'border-transparent hover:bg-app-row-hover'
            }`}
          >
            <input
              type="checkbox"
              checked={checked.has(s.id)}
              onClick={e => e.stopPropagation()}
              onChange={() => toggle(s.id)}
              className="flex-shrink-0 accent-blue-600"
            />
            <div className="min-w-0 flex-1">
              <div className={`text-xs truncate ${s.id === selectedId ? 'text-white' : 'text-gray-300'}`}>
                {s.name || <span className="italic text-gray-600">(unnamed)</span>}
              </div>
              <div className="text-[10px] text-gray-600">
                {s.code ? `${s.code.split('\n').length} lines` : 'empty'}
              </div>
            </div>
            {dupes.has(s.name) && (
              <span
                title="Another script has this exact name — ScriptStart can't tell them apart"
                className="text-amber-500 text-xs flex-shrink-0"
              >
                ⚠
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ScriptToolbar({ script, duplicateName, view, setView, onRename, rowCount, functionCount }) {
  const tab = active =>
    `px-3 py-1 text-xs rounded transition-colors ${
      active ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-app-fg'
    }`;

  return (
    <div className="flex items-center gap-3 px-4 py-2 border-b border-app-border flex-shrink-0">
      <input
        type="text"
        value={script.name}
        onChange={e => onRename(e.target.value)}
        placeholder="Script name"
        className="bg-app-surface border border-app-border rounded px-2 py-1 text-sm text-app-fg w-72 focus:outline-none focus:border-blue-500"
      />
      {duplicateName && (
        <span
          title="Another script has this exact name. vMix's ScriptStart takes a name, so it can't tell them apart."
          className="text-[11px] text-amber-500"
        >
          ⚠ duplicate name
        </span>
      )}

      <span className="text-[11px] text-gray-600">
        {rowCount} {view === 'actions' ? 'step' : 'line'}{rowCount === 1 ? '' : 's'}
        {functionCount != null && ` · ${functionCount} vMix call${functionCount === 1 ? '' : 's'}`}
      </span>

      <div className="ml-auto flex items-center gap-1 bg-app-surface border border-app-border rounded p-0.5">
        <button className={tab(view === 'actions')} onClick={() => setView('actions')}>Actions</button>
        <button className={tab(view === 'raw')} onClick={() => setView('raw')}>Raw</button>
      </div>
    </div>
  );
}

// The function creator: pick a vMix and a function the same way the cue editor
// does, then drop the correctly formatted call into the script.
function BuilderBar({ instances, onInsert, view }) {
  const [fn, setFn] = useState('');
  const [input, setInput] = useState('');
  const [value, setValue] = useState('');
  const [targetKey, setTargetKey] = useState('');

  const cell = 'bg-app-surface border border-app-border rounded px-2 py-1 text-xs text-app-fg focus:outline-none focus:border-blue-500';

  function insert() {
    if (!fn.trim()) return;
    let target = null;
    if (targetKey) {
      const [host, port] = targetKey.split(':');
      target = { host, port: Number(port) };
    }
    onInsert({ fn: fn.trim(), input: input.trim(), value: value.trim(), target });
    setInput('');
    setValue('');
  }

  return (
    <div className="flex items-center gap-2 px-4 py-2 border-b border-app-border bg-app-well flex-shrink-0">
      <span className="text-[11px] text-gray-500 uppercase tracking-wide flex-shrink-0">Build</span>

      <select value={targetKey} onChange={e => setTargetKey(e.target.value)} className={`w-40 ${cell}`}>
        <option value="">This vMix</option>
        {instances.map(i => (
          <option key={i.id} value={`${i.host}:${i.port}`}>{i.label}</option>
        ))}
      </select>

      <FunctionCombobox value={fn} onChange={setFn} autoFocus={false} className="w-56" placeholder="Function…" />

      <input value={input} onChange={e => setInput(e.target.value)} placeholder="Input" className={`w-24 ${cell}`} />
      <input
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder="Value"
        onKeyDown={e => { if (e.key === 'Enter') insert(); }}
        className={`flex-1 min-w-0 ${cell}`}
      />

      <button
        onClick={insert}
        disabled={!fn.trim()}
        title={view === 'raw' ? 'Insert at the cursor' : 'Insert after the last ticked step'}
        className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:hover:bg-blue-600 text-white transition-colors flex-shrink-0"
      >
        Insert
      </button>

      {targetKey && (
        <span
          title="Calls to another vMix become a WebRequest block — vMix's own API.Function only reaches the machine the script runs on."
          className="text-[10px] text-gray-600 flex-shrink-0"
        >
          via Web API
        </span>
      )}
    </div>
  );
}

// Retarget or delete every ticked step at once — the reason this view exists.
function BulkRowBar({ count, instances, rows, checkedRows, setCheckedRows, updateRows }) {
  // Show what the ticked steps actually target, so the dropdown can't imply
  // "This vMix" over a selection that is already pointed somewhere else.
  const picked = rows.filter(r => checkedRows.has(r.id) && r.kind === 'function');
  const keys = new Set(picked.map(r => (r.target ? `${r.target.host}:${r.target.port}` : '')));
  const target = keys.size === 1 ? [...keys][0] : 'mixed';

  function retarget(key) {
    const next = rows.map(r => {
      if (!checkedRows.has(r.id) || r.kind !== 'function') return r;
      if (!key) return { ...r, target: null, style: r.style === 'webrequest' ? 'named' : r.style, pristine: false };
      const [host, port] = key.split(':');
      return { ...r, target: { host, port: Number(port) }, style: 'webrequest', pristine: false };
    });
    updateRows(next);
  }

  return (
    <div className="flex items-center gap-2 px-4 py-1.5 bg-blue-950/40 border-b border-blue-900 flex-shrink-0">
      <span className="text-[11px] text-blue-300">{count} step{count === 1 ? '' : 's'} selected</span>

      <span className="text-[11px] text-gray-500 ml-2">Send to</span>
      <select
        value={target}
        onChange={e => retarget(e.target.value)}
        className="bg-app-surface border border-app-border rounded px-2 py-0.5 text-xs text-app-fg focus:outline-none focus:border-blue-500"
      >
        {target === 'mixed' && <option value="mixed" disabled>Mixed</option>}
        <option value="">This vMix</option>
        {instances.map(i => (
          <option key={i.id} value={`${i.host}:${i.port}`}>{i.label}</option>
        ))}
        {/* A host in the script but not in the registry, so selecting it back is possible. */}
        {[...keys].filter(k => k && !instances.some(i => `${i.host}:${i.port}` === k)).map(k => (
          <option key={k} value={k}>{k} (unregistered)</option>
        ))}
      </select>

      <button
        onClick={() => {
          // Copies land together just after the last selected step.
          const idx = rows.reduce((acc, r, i) => (checkedRows.has(r.id) ? i : acc), -1);
          const copies = rows.filter(r => checkedRows.has(r.id)).map(cloneRow);
          const next = [...rows];
          next.splice(idx + 1, 0, ...copies);
          updateRows(next);
          setCheckedRows(new Set());
        }}
        className="text-[11px] text-gray-400 hover:text-app-fg ml-2"
      >
        Duplicate
      </button>
      <button
        onClick={() => {
          updateRows(rows.filter(r => !checkedRows.has(r.id)));
          setCheckedRows(new Set());
        }}
        className="text-[11px] text-gray-400 hover:text-red-400"
      >
        Delete
      </button>
      <button onClick={() => setCheckedRows(new Set())} className="ml-auto text-[11px] text-gray-500 hover:text-app-fg">
        Clear selection
      </button>
    </div>
  );
}

// ------------------------------------------------------------ find/replace --

function FindReplaceModal({ scripts, checked, selectedId, onClose, onApply }) {
  const [find, setFind] = useState('');
  const [replace, setReplace] = useState('');
  const [scope, setScope] = useState(checked.size > 0 ? 'checked' : 'all');
  const [useRegex, setUseRegex] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(true);
  const [alsoNames, setAlsoNames] = useState(false);

  const inScope = useMemo(() => {
    if (scope === 'checked') return scripts.filter(s => checked.has(s.id));
    if (scope === 'current') return scripts.filter(s => s.id === selectedId);
    return scripts;
  }, [scope, scripts, checked, selectedId]);

  // Build the matcher once, and surface a bad regex rather than throwing on
  // every keystroke.
  const { re, regexError } = useMemo(() => {
    if (!find) return { re: null, regexError: '' };
    try {
      const pattern = useRegex ? find : find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return { re: new RegExp(pattern, caseSensitive ? 'g' : 'gi'), regexError: '' };
    } catch (err) {
      return { re: null, regexError: err.message };
    }
  }, [find, useRegex, caseSensitive]);

  const hits = useMemo(() => {
    if (!re) return [];
    return inScope
      .map(s => {
        const codeHits = (s.code.match(new RegExp(re.source, re.flags)) || []).length;
        const nameHits = alsoNames ? (s.name.match(new RegExp(re.source, re.flags)) || []).length : 0;
        return { script: s, codeHits, nameHits };
      })
      .filter(h => h.codeHits + h.nameHits > 0);
  }, [re, inScope, alsoNames]);

  const total = hits.reduce((n, h) => n + h.codeHits + h.nameHits, 0);

  function apply() {
    if (!re || total === 0) return;
    const ids = new Set(inScope.map(s => s.id));
    onApply(scripts.map(s => {
      if (!ids.has(s.id)) return s;
      return {
        ...s,
        code: s.code.replace(new RegExp(re.source, re.flags), replace),
        name: alsoNames ? s.name.replace(new RegExp(re.source, re.flags), replace) : s.name,
      };
    }));
  }

  const cell = 'w-full bg-app-surface border border-app-border rounded px-2 py-1.5 text-xs text-app-fg font-mono focus:outline-none focus:border-blue-500';

  return (
    <Modal title="Find & Replace" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Find</label>
          <input value={find} onChange={e => setFind(e.target.value)} autoFocus className={cell} spellCheck={false} />
        </div>
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Replace with</label>
          <input value={replace} onChange={e => setReplace(e.target.value)} className={cell} spellCheck={false} />
        </div>

        <div className="flex flex-wrap items-center gap-4 text-[11px] text-gray-400">
          <label className="flex items-center gap-1.5">
            <span className="text-gray-500">Across</span>
            <select
              value={scope}
              onChange={e => setScope(e.target.value)}
              className="bg-app-surface border border-app-border rounded px-1.5 py-0.5 text-app-fg focus:outline-none focus:border-blue-500"
            >
              <option value="all">All {scripts.length} scripts</option>
              <option value="checked" disabled={checked.size === 0}>
                {checked.size} ticked script{checked.size === 1 ? '' : 's'}
              </option>
              <option value="current">This script only</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={useRegex} onChange={e => setUseRegex(e.target.checked)} className="accent-blue-600" />
            Regex
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={caseSensitive} onChange={e => setCaseSensitive(e.target.checked)} className="accent-blue-600" />
            Match case
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={alsoNames} onChange={e => setAlsoNames(e.target.checked)} className="accent-blue-600" />
            Also script names
          </label>
        </div>

        {regexError && <div className="text-xs text-red-400">Invalid regex: {regexError}</div>}

        <div className="border border-app-border rounded max-h-56 overflow-y-auto">
          {!find ? (
            <div className="px-3 py-6 text-center text-xs text-gray-600">
              Type something to find. Nothing is changed until you hit Replace.
            </div>
          ) : total === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-gray-600">No matches.</div>
          ) : (
            hits.map(h => (
              <div key={h.script.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-app-border last:border-0">
                <span className="text-xs text-gray-300 truncate flex-1">{h.script.name}</span>
                <span className="text-[10px] text-gray-500 flex-shrink-0">
                  {h.codeHits > 0 && `${h.codeHits} in code`}
                  {h.codeHits > 0 && h.nameHits > 0 && ', '}
                  {h.nameHits > 0 && `${h.nameHits} in name`}
                </span>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-500">
            {total > 0 && `${total} match${total === 1 ? '' : 'es'} in ${hits.length} script${hits.length === 1 ? '' : 's'}`}
          </span>
          <button onClick={onClose} className="ml-auto px-3 py-1.5 text-xs rounded border border-app-border text-gray-400 hover:text-app-fg">
            Cancel
          </button>
          <button
            onClick={apply}
            disabled={total === 0}
            className="px-3 py-1.5 text-xs rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white transition-colors"
          >
            Replace {total > 0 ? total : ''}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// -------------------------------------------------------------- test run --

function RunModal({ script, instances, onClose }) {
  const [shows, setShows] = useState([]);
  const [showId, setShowId] = useState('');
  const [instanceId, setInstanceId] = useState('');
  const [state, setState] = useState('idle');   // idle | running | ok | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    showsAPI.list().then(r => setShows(r.data || [])).catch(() => {});
  }, []);

  async function run() {
    setState('running');
    setMessage('');
    try {
      await vmixAPI.scriptRun(showId, instanceId, script.code);
      setState('ok');
      setMessage('Script sent.');
    } catch (err) {
      setState('error');
      setMessage(err.response?.data?.error || err.message || 'Failed to run the script.');
    }
  }

  return (
    <Modal title={`Test run "${script.name}"`} onClose={onClose}>
      <div className="space-y-3">
        <div className="px-3 py-2 bg-red-950/40 border border-red-900 rounded text-[11px] text-red-300 leading-relaxed">
          This runs the script on a real vMix immediately. Anything it puts to air goes to air.
          The script is <strong>not</strong> saved into that vMix — vMix has no API for that, so the
          preset file is still the only way to install it.
        </div>

        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Relay via show</label>
          <select
            value={showId}
            onChange={e => setShowId(e.target.value)}
            className="w-full bg-app-surface border border-app-border rounded px-2 py-1.5 text-xs text-app-fg focus:outline-none focus:border-blue-500"
          >
            <option value="">Choose a show…</option>
            {shows.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <p className="text-[10px] text-gray-600 mt-1 leading-relaxed">
            A vMix on the venue LAN isn't reachable from the cloud, so the call goes out through
            that show's LocalBridge. Pick a show whose bridge is running on the same network.
          </p>
        </div>

        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Run on</label>
          <select
            value={instanceId}
            onChange={e => setInstanceId(e.target.value)}
            className="w-full bg-app-surface border border-app-border rounded px-2 py-1.5 text-xs text-app-fg focus:outline-none focus:border-blue-500"
          >
            <option value="">The show's own vMix (Primary)</option>
            {instances.map(i => (
              <option key={i.id} value={i.id}>{i.label} ({i.host}:{i.port})</option>
            ))}
          </select>
        </div>

        {message && (
          <div className={`text-xs ${state === 'ok' ? 'text-green-400' : 'text-red-400'}`}>{message}</div>
        )}

        <div className="flex items-center gap-2">
          <button onClick={onClose} className="ml-auto px-3 py-1.5 text-xs rounded border border-app-border text-gray-400 hover:text-app-fg">
            Close
          </button>
          <button
            onClick={run}
            disabled={!showId || state === 'running'}
            className="px-3 py-1.5 text-xs rounded bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:hover:bg-red-600 text-white transition-colors"
          >
            {state === 'running' ? 'Running…' : 'Run now'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- splicing --

// Downloading a library preset needs the operator's own file back, because the
// server only ever held the <Scripting> block. This is the visible cost of not
// storing stream keys, so it says so rather than looking like a missing feature.
function SpliceModal({ library, onClose, onFile }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);

  return (
    <Modal title="Choose your preset file" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-[11px] text-gray-400 leading-relaxed">
          Only the scripts were stored — inputs, outputs and streaming settings never left your
          machine, which is why they are not here to download. Pick your own
          {library?.file_name ? <> <span className="text-gray-300 font-mono">{library.file_name}</span></> : ' .vmix file'} and
          the edited scripts will be spliced into it. Every other byte comes from your copy.
        </p>

        <div
          onDragOver={e => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={e => {
            e.preventDefault();
            setOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) onFile(f);
          }}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg px-6 py-10 text-center cursor-pointer transition-colors ${
            over ? 'border-blue-500 bg-blue-950/20' : 'border-app-border hover:border-app-border-strong'
          }`}
        >
          <div className="text-app-fg text-sm font-medium">Drop your .vmix here</div>
          <div className="text-gray-600 text-xs mt-1">or click to choose it</div>
          <input
            ref={inputRef}
            type="file"
            accept=".vmix,.xml,.txt"
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
          />
        </div>

        <p className="text-[10px] text-gray-600 leading-relaxed">
          If it isn’t the file these scripts came from, you’ll get a warning before anything is
          written — the inputs may not line up.
        </p>

        <div className="flex">
          <button onClick={onClose} className="ml-auto px-3 py-1.5 text-xs rounded border border-app-border text-gray-400 hover:text-app-fg">
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        className="bg-app-panel border border-app-line-chrome rounded-lg shadow-xl w-full max-w-lg"
      >
        <div className="flex items-center px-4 py-2.5 border-b border-app-border">
          <h2 className="text-sm font-medium text-white">{title}</h2>
          <button onClick={onClose} className="ml-auto text-gray-500 hover:text-app-fg text-sm">✕</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
